import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Target, FileText, Sprout, TrendingUp, DollarSign, ShieldAlert, Zap, BookOpen, ArrowRight } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  leaf: Leaf,
  target: Target,
  'file-text': FileText,
  sprout: Sprout,
  'trending-up': TrendingUp,
  'dollar-sign': DollarSign,
  'shield-alert': ShieldAlert,
  zap: Zap,
  'book-open': BookOpen
};

const process = [
  { step: '01', title: 'Assessment', description: 'Comprehensive carbon footprint analysis' },
  { step: '02', title: 'Strategy', description: 'Custom roadmap aligned with goals' },
  { step: '03', title: 'Implementation', description: 'Execute verified methodologies' },
  { step: '04', title: 'Verification', description: 'Third-party certification' }
];

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="services-page bg-white" data-testid="services-page">
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#FAFAFA] border-b border-zinc-200">
        <div className="container mx-auto">
          <div className="max-w-3xl">
            <div className="accent-line mb-6" />
            <span className="overline">Our Capabilities</span>
            <h1 className="text-[#09090B] mt-4 mb-6">Comprehensive Climate Solutions</h1>
            <p className="text-lg md:text-xl text-[#52525B] leading-relaxed">
              End-to-end sustainability services that drive measurable environmental and business impact
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 auto-rows-fr border-t border-l border-zinc-200">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <div
                  key={service.id}
                  className="flex flex-col h-full p-8 border-b border-r border-zinc-200 hover:bg-[#FAFAFA] transition-colors duration-200 group"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="w-14 h-14 bg-[#047857]/10 flex items-center justify-center mb-6 group-hover:bg-[#047857] transition-colors duration-200">
                    <Icon className="w-7 h-7 text-[#047857] group-hover:text-white transition-colors duration-200" />
                  </div>
                  <h3 className="text-xl font-bold text-[#09090B] mb-3">{service.title}</h3>
                  <p className="text-[#52525B] mb-6 leading-relaxed line-clamp-3">{service.description}</p>
                  <ul className="space-y-2 mb-6 flex-grow">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-sm text-[#52525B]">
                        <div className="w-1.5 h-1.5 bg-[#047857] mt-1.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="text-[#047857] font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all mt-auto" data-testid={`service-learn-more-${service.id}`}>
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-28 bg-[#FAFAFA] border-y border-zinc-200">
        <div className="container mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="accent-line mb-6" />
            <h2 className="text-[#09090B] mb-4">Our Approach</h2>
            <p className="text-lg text-[#52525B]">
              A proven methodology that delivers measurable results
            </p>
          </div>

          <div className="grid md:grid-cols-4 border-t border-l border-zinc-200 bg-white">
            {process.map((item, index) => (
              <div key={index} className="p-8 border-b border-r border-zinc-200">
                <div className="font-display text-5xl font-extrabold text-[#047857]/20 mb-4">{item.step}</div>
                <h3 className="text-lg font-bold text-[#09090B] mb-2">{item.title}</h3>
                <p className="text-[#52525B] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto">
          <div className="relative bg-[#09090B] text-white p-12 md:p-20 text-center overflow-hidden">
            <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-white mb-6">Ready to Transform Your Sustainability Strategy?</h2>
              <p className="text-lg text-zinc-300 mb-10">
                Schedule a consultation with our experts to discuss your specific needs.
              </p>
              <a
                href="https://calendly.com/shailendra-creduce/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="services-schedule-btn"
              >
                <span>Schedule Consultation</span>
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
